import type { ReactNode } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  CoachSessionPrepVeeraBrief,
  NextSessionBrief,
  VisibilityState,
} from "@/lib/behavior/viewModels/coachSessionPrepVeera";

interface CoachVeeraPrepBriefCardProps {
  brief: CoachSessionPrepVeeraBrief;
  savedCarryForwardNote?: string | null;
  reviewedCarryForward?: Array<{ id: string; title: string }>;
  carryForwardLoadError?: string | null;
  isLoadingCarryForward?: boolean;
  onRetryCarryForward?: () => void;
  carryForwardUpdatedAt?: string | null;
  carryForwardNote?: string;
  onCarryForwardNoteChange?: (note: string) => void;
  onSaveCarryForwardNote?: () => void;
  isSavingCarryForwardNote?: boolean;
  visibilityState?: VisibilityState;
  onVisibilityStateChange?: (visibilityState: VisibilityState) => void;
  onOpenEvidenceReference?: (reference: {
    label: string;
    link: string | null;
    evidenceId?: string | null;
    sourceRevision?: string;
    source?: "coach_veera_brief";
  }) => void;
}

function BulletList({ items, emptyLabel }: { items: string[]; emptyLabel?: string }) {
  if (items.length === 0) {
    return emptyLabel ? <p className="text-sm leading-6 text-muted-foreground">{emptyLabel}</p> : null;
  }

  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {items.map((item) => (
        <li key={item} className="text-sm leading-6 text-foreground">
          {item}
        </li>
      ))}
    </ul>
  );
}

function SectionBlock({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md border border-border/60 bg-muted/10 p-3">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function visibilityLabel(visibility: NextSessionBrief["visibility"]) {
  switch (visibility) {
    case "leader_safe":
      return "Leader-safe";
    case "coach_reviewed":
      return "Coach-reviewed";
    case "not_shareable_yet":
      return "Not shareable yet";
    case "coach_only":
    default:
      return "Coach-only";
  }
}

const VISIBILITY_OPTIONS: Array<{
  value: VisibilityState;
  label: string;
  description: string;
}> = [
  {
    value: "coach_only",
    label: "Coach-only",
    description: "Private preparation state for the coach.",
  },
  {
    value: "coach_reviewed",
    label: "Coach-reviewed",
    description: "Reviewed by the coach, still not shared automatically.",
  },
  {
    value: "leader_safe",
    label: "Leader-safe",
    description: "Marked as safe to reference with the leader, but still requires coach choice.",
  },
  {
    value: "not_shareable_yet",
    label: "Not shareable yet",
    description: "Needs more evidence or coach review before any leader-facing use.",
  },
];

function confidenceLabel(confidence: NextSessionBrief["evidenceBackedMoments"][number]["confidence"]) {
  return confidence[0].toUpperCase() + confidence.slice(1);
}

function formatBriefTimestamp(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return format(parsed, "MMM d, yyyy");
}

export function CoachVeeraPrepBriefCard({
  brief,
  savedCarryForwardNote = null,
  reviewedCarryForward = [],
  carryForwardLoadError = null,
  isLoadingCarryForward = false,
  onRetryCarryForward,
  carryForwardUpdatedAt = null,
  carryForwardNote = "",
  onCarryForwardNoteChange,
  onSaveCarryForwardNote,
  isSavingCarryForwardNote = false,
  visibilityState,
  onVisibilityStateChange,
  onOpenEvidenceReference,
}: CoachVeeraPrepBriefCardProps) {
  const nextBrief = brief.nextSessionBrief;
  const effectiveVisibility = visibilityState ?? nextBrief.visibility;
  const activeVisibilityOption = VISIBILITY_OPTIONS.find((option) => option.value === effectiveVisibility)
    ?? VISIBILITY_OPTIONS[0];
  const savedNote = savedCarryForwardNote
    ?? (!carryForwardLoadError && !isLoadingCarryForward ? nextBrief.carryForward?.note : null)
    ?? null;
  const canEditCarryForward = Boolean(onCarryForwardNoteChange && onSaveCarryForwardNote);
  const canSaveCarryForward = carryForwardNote.trim().length > 0 && !isSavingCarryForwardNote
    && !carryForwardLoadError && !isLoadingCarryForward;
  const evidenceBackedMoments = nextBrief.evidenceBackedMoments.length > 0
    ? nextBrief.evidenceBackedMoments
    : [{
        id: "empty-evidence-state",
        title: "Evidence still thin",
        observedFact: "Available evidence is thin, so this brief should be treated as a coach-review starting point.",
        confidence: "low" as const,
        visibility: "coach_only" as const,
      }];

  return (
    <Card className="border-border/60 bg-background/85">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base">Next Session Brief</CardTitle>
            <p className="text-sm leading-6 text-muted-foreground">
              Evidence-backed prep for the next coach conversation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{visibilityLabel(effectiveVisibility)}</Badge>
            <Badge variant="outline">Human review required</Badge>
            {effectiveVisibility !== "not_shareable_yet" ? (
              <Badge variant="outline">Not shared automatically</Badge>
            ) : (
              <Badge variant="outline">Not shareable yet</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        {brief.coachContradictionProbes?.length ? <SectionBlock title="Questions to test contradictory evidence">
          <BulletList items={brief.coachContradictionProbes} />
          <p className="mt-2 text-xs text-muted-foreground">Private coach preparation. These questions are not shared with the leader.</p>
        </SectionBlock> : null}
        {brief.practiceContext?.items.length ? <SectionBlock title="Practice context">
          <BulletList items={brief.practiceContext.items.map(item=>`${item.state==='chosen'?'Chosen practice':item.state==='declaration'?'Leader declaration':item.state==='reported_attempt'?'Reported attempt':'Practice suggestion'}: ${item.title} — ${item.instruction}`)} />
        </SectionBlock> : null}
        <SectionBlock title="Since last session">
          <p className="text-sm leading-6 text-foreground">{nextBrief.sinceLastSession.summary}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {nextBrief.sinceLastSession.evidenceCount} evidence reference
            {nextBrief.sinceLastSession.evidenceCount === 1 ? "" : "s"}
            {nextBrief.sinceLastSession.lastObservedAt
              ? ` since ${formatBriefTimestamp(nextBrief.sinceLastSession.lastObservedAt)}`
              : ""}
          </p>
        </SectionBlock>

        <SectionBlock title="Evidence-backed moments">
          <div className="space-y-3">
            {evidenceBackedMoments.map((moment) => {
              const reference = brief.evidenceRefs.find((entry) => entry.label === moment.sourceLabel)
                || brief.evidenceRefs.find((entry) => entry.id === moment.id);

              return (
                <div key={moment.id} className="space-y-1.5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm font-medium text-foreground">{moment.title}</p>
                    <Badge variant="outline">{confidenceLabel(moment.confidence)}</Badge>
                  </div>
                  <p className="text-sm leading-6 text-foreground">{moment.observedFact}</p>
                  {reference ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenEvidenceReference?.({
                        label: reference.label,
                        link: reference.route,
                        evidenceId: reference.id,
                        sourceRevision: reference.sourceRevision,
                        source: "coach_veera_brief",
                      })}
                    >
                      {reference.label}
                    </Button>
                  ) : moment.sourceLabel ? (
                    <p className="text-xs text-muted-foreground">Source: {moment.sourceLabel}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Evidence is thin for this item.</p>
                  )}
                </div>
              );
            })}
          </div>
        </SectionBlock>

        <SectionBlock title="Possible coaching edge">
          {nextBrief.possibleCoachingEdge ? (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-foreground">{nextBrief.possibleCoachingEdge.label}</p>
              <p className="text-sm leading-6 text-foreground">{nextBrief.possibleCoachingEdge.rationale}</p>
            </div>
          ) : (
            <p className="text-sm leading-6 text-muted-foreground">
              Evidence is still thin, so no specific coach-review edge is named yet.
            </p>
          )}
        </SectionBlock>

        <SectionBlock title="What is not known">
          <BulletList items={nextBrief.whatIsNotKnown} />
        </SectionBlock>

        <SectionBlock title="Suggested coach questions">
          <BulletList items={nextBrief.suggestedCoachQuestions} />
        </SectionBlock>

        <SectionBlock title="Carry forward">
          <div className="space-y-3">
            {carryForwardLoadError ? (
              <div role="alert" className="space-y-2">
                <p className="text-sm text-muted-foreground">Carry-forward context could not be loaded.</p>
                {onRetryCarryForward ? <Button type="button" variant="outline" size="sm" onClick={onRetryCarryForward}>Retry carry-forward</Button> : null}
              </div>
            ) : isLoadingCarryForward ? <p role="status" className="text-sm text-muted-foreground">Loading carry-forward context...</p> : null}
            {reviewedCarryForward.length > 0 ? (
              <div>
                <p className="text-xs text-muted-foreground">Coach continuity review</p>
                <ul className="list-disc space-y-1 pl-5">
                  {reviewedCarryForward.map((review) => (
                    <li key={review.id} className="text-sm leading-6 text-foreground">{review.title}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {savedNote ? (
              <div className="space-y-1">
                <p className="text-sm leading-6 text-foreground">{savedNote}</p>
                {carryForwardUpdatedAt ? (
                  <p className="text-xs text-muted-foreground">
                    Updated {formatBriefTimestamp(carryForwardUpdatedAt)}
                  </p>
                ) : null}
              </div>
            ) : !carryForwardLoadError && !isLoadingCarryForward ? (
              <p className="text-sm leading-6 text-muted-foreground">
                No carry-forward note is saved in this brief yet.
              </p>
            ) : null}
            {canEditCarryForward ? (
              <div className="space-y-2">
                <label htmlFor="carry-forward-coach-note" className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Carry-forward coach note
                </label>
                <Textarea
                  id="carry-forward-coach-note"
                  value={carryForwardNote}
                  onChange={(event) => onCarryForwardNoteChange?.(event.target.value)}
                  rows={3}
                  placeholder="Coach-only note for the next session"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={onSaveCarryForwardNote}
                  disabled={!canSaveCarryForward}
                >
                  {savedCarryForwardNote ? "Update carry-forward note" : "Save carry-forward note"}
                </Button>
              </div>
            ) : null}
          </div>
        </SectionBlock>

        <SectionBlock title="Visibility">
          <div className="space-y-3">
            {onVisibilityStateChange ? (
              <div className="space-y-2">
                <label htmlFor="brief-visibility-state" className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Brief visibility state
                </label>
                <Select
                  value={effectiveVisibility}
                  onValueChange={(value) => onVisibilityStateChange(value as VisibilityState)}
                  disabled={isSavingCarryForwardNote}
                >
                  <SelectTrigger id="brief-visibility-state" aria-label="Brief visibility state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <p className="text-sm leading-6 text-foreground">
              {visibilityLabel(effectiveVisibility)}. {activeVisibilityOption.description} Nothing becomes leader-visible automatically.
            </p>
          </div>
        </SectionBlock>
      </CardContent>
    </Card>
  );
}
